import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { resolveDmaName } from '../../components/self-service/targetingRow';
import { Loader2, ArrowLeft, Check, X, ExternalLink, Building2, Globe, DollarSign, Calendar, Tv, MapPin } from 'lucide-react';
import { REJECTION_REASONS, REJECTION_REASONS_BY_CODE } from '../../components/self-service/rejectionReasons';
import type { SelfServiceCreativeRow, SelfServiceSubmissionRow } from '../../types/selfService';
import { publisherStatusBadge } from '../../components/self-service/campaignSharedHelpers';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import AdminPageHeader from '../../components/self-service/admin/AdminPageHeader';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { rankOf } from '../../components/self-service/shared/roleRank';
import { apiClient } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import { formatMoneySmart, ensureAbsoluteUrl } from '../../components/self-service/shared/format';
import { formatDate } from '../../components/self-service/shared/dateFormat';

type SubmissionRow = SelfServiceSubmissionRow;

// CTV-457 Phase C — target of the reject modal. Per-creative reject is the
// single reject surface now (CTV-658): every pending card carries its own
// Reject; there is no separate submission-level reject button.
type RejectTarget = { kind: 'creative'; creativeId: string };

const PublisherSubmissionDetailPage: React.FC = () => {
  const { slug, config } = useSingleStationContext();
  const { user } = useAuth();
  const { submissionId } = useParams<{ submissionId: string }>();
  // CTV-673 audit: submission review is manager+ — the API refuses
  // sales_rep on every submissions route, so don't render (or fetch).
  if (rankOf(user?.rawRole) < rankOf('manager')) {
    return <Navigate to={`/admin/${slug}`} replace />;
  }
  if (!submissionId) return <Navigate to={`/admin/${slug}/submissions`} replace />;
  return <Inner slug={slug} submissionId={submissionId} config={config} />;
};

const Inner: React.FC<{ slug: string; submissionId: string; config: import('../../types/selfService').PublisherConfig }> = ({ slug, submissionId, config }) => {
  const [row, setRow] = React.useState<SubmissionRow | null>(null);
  // CTV-457 Phase C: video URLs are per-creative, one entry per reviewable row.
  const [videoUrls, setVideoUrls] = React.useState<Record<string, string>>({});
  // Latest videoUrls, so a background poll can skip re-signing videos it
  // already has (a fresh signed URL would reload the <video> and flicker it).
  const videoUrlsRef = React.useRef<Record<string, string>>({});
  const [loading, setLoading] = React.useState(true);
  const [actionBusy, setActionBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = React.useState<RejectTarget | null>(null);
  const [rejectReason, setRejectReason] = React.useState('');
  const [rejectCodes, setRejectCodes] = React.useState<string[]>([]);

  // ADR CTV-732: Publica-free publisher review. The upload checkbox
  // gate, the embedded app.getpublica.com iframe, the per-card download, and
  // the readiness banners are all gone — station staff now do pure content
  // review, and internal CTV staff own every Publica interaction from
  // /admin/publica-uploads.
  //
  // Confirmation modal shown before the irreversible first-review charge.
  const [chargeConfirmOpen, setChargeConfirmOpen] = React.useState(false);
  // CTV-466: confirmation modal for "Send rejections to advertiser".
  const [sendRejectionsConfirmOpen, setSendRejectionsConfirmOpen] = React.useState(false);

  // CTV-457 Phase C: the set of creatives the publisher reviews on this
  // page — active (already approved), pending_review (awaiting decision),
  // and UNRESOLVED voided. API returns them sorted active → pending_review →
  // voided → retired.
  const reviewableCreatives: SelfServiceCreativeRow[] = React.useMemo(
    () =>
      (row?.creatives ?? []).filter(
        (c) =>
          c.status === 'active' ||
          c.status === 'pending_review' ||
          (c.status === 'voided' && !c.resolved_at),
      ),
    [row?.creatives],
  );

  // CTV-732: the only thing this page acts on now is undecided creatives.
  // A failed Publica push is no longer a publisher concern (it surfaces on the
  // admin worklist as failed verification), so there is no active-failure
  // branch and no "actionable" superset.
  const pendingCreatives = reviewableCreatives.filter((c) => c.status === 'pending_review');

  const isFirstReview = row?.status === 'payment_authorized';
  const isPostApprovalSwap = row?.status === 'approved' && pendingCreatives.length > 0;
  // There are creatives to approve (first review or a post-approval swap).
  const canApprove = isFirstReview || isPostApprovalSwap;

  const refresh = React.useCallback(async (opts?: { quiet?: boolean }) => {
    // A background poll passes quiet so it doesn't flip the whole page to the
    // loading spinner while a push settles.
    if (!opts?.quiet) setLoading(true);
    try {
      const { rows } = await apiClient.post<{ rows: SubmissionRow[] }>(
        '/submissions/list-by-publisher',
        { slug },
      );
      const found = (rows ?? []).find((r) => r.id === submissionId) ?? null;
      setRow(found);

      // Per-creative view-url fetch. CTV-611: mirror reviewableCreatives so
      // resolved rejections don't get signed URLs fetched either. Only fetch
      // for creatives we don't already have a URL for, so a poll never re-signs
      // an existing video and forces the player to reload.
      const reviewable = (found?.creatives ?? []).filter(
        (c) =>
          c.status === 'active' ||
          c.status === 'pending_review' ||
          (c.status === 'voided' && !c.resolved_at),
      );
      const existing = videoUrlsRef.current;
      const missing = reviewable.filter((c) => !!c.storage_path && !existing[c.id]);
      if (missing.length > 0) {
        const results = await Promise.allSettled(
          missing.map(async (c) => {
            const { url } = await apiClient.post<{ url: string }>('/creatives/view-url', {
              submissionId: found!.id,
              creativeId: c.id,
            });
            return [c.id, url] as const;
          }),
        );
        const urls: Record<string, string> = {};
        for (const r of results) {
          if (r.status === 'fulfilled') urls[r.value[0]] = r.value[1];
        }
        if (Object.keys(urls).length > 0) {
          setVideoUrls((prev) => ({ ...prev, ...urls }));
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      if (!opts?.quiet) setLoading(false);
    }
  }, [slug, submissionId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Keep the URL ref in sync so refresh() can dedupe already-signed videos.
  React.useEffect(() => { videoUrlsRef.current = videoUrls; }, [videoUrls]);

  // Approve every pending creative. First review captures payment; a running
  // campaign promotes each pending creative and (once the files are in
  // Publica's library) enqueues a swap push. CTV-732 removed the auto-retry
  // tail: publishers no longer drive Publica recovery.
  const approveAll = async () => {
    if (!row) return;
    setActionBusy(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${row.id}/creatives/approve-all`, {});
      // First review captures the advertiser's payment inside approve-all;
      // keep the PAYMENT_CAPTURED signal alongside the approval event.
      if (isFirstReview) {
        analytics.track(EVENTS.PAYMENT_CAPTURED, { station_slug: slug, submission_id: row.id });
      }
      analytics.track(EVENTS.CREATIVE_APPROVED, { station_slug: slug, submission_id: row.id, scope: 'all' });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed');
    } finally { setActionBusy(false); }
  };

  // Approve ONE creative, flipping it pending_review -> active.
  //
  // This path never charges, by construction: the caller routes the one click
  // that WOULD trigger capture (the last undecided commercial on a first
  // review with no rejections) through the charge-confirmation modal instead —
  // see `perCardApproveWouldCharge`. Everything reaching here is either a
  // mixed verdict, where the API's voided-row capture blocker holds the money,
  // or a running campaign, where there is nothing left to charge.
  const approveOneCreative = async (creativeId: string) => {
    if (!row) return;
    setActionBusy(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${row.id}/creatives/${creativeId}/approve`, {});
      analytics.track(EVENTS.CREATIVE_APPROVED, {
        station_slug: slug,
        submission_id: row.id,
        creative_id: creativeId,
        scope: 'one',
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed');
    } finally { setActionBusy(false); }
  };

  const sendRejections = async () => {
    if (!row) return;
    setActionBusy(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${row.id}/finalize-rejections`, {});
      analytics.track(EVENTS.SUBMISSION_REJECTED, {
        station_slug: slug,
        submission_id: row.id,
        rejected_creative_count: reviewableCreatives.filter((c) => c.status === 'voided').length,
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send rejections failed');
    } finally { setActionBusy(false); }
  };

  const rejectHasOtherCode = rejectCodes.includes('other');
  const rejectCanSubmit =
    rejectCodes.length > 0 && (!rejectHasOtherCode || rejectReason.trim().length > 0);

  const submitReject = async () => {
    if (!row || !rejectTarget || !rejectCanSubmit) return;
    setActionBusy(true);
    setError(null);
    try {
      await apiClient.post(
        `/submissions/${row.id}/creatives/${rejectTarget.creativeId}/reject`,
        { reasonCodes: rejectCodes, reason: rejectReason.trim() || undefined },
      );
      analytics.track(EVENTS.CREATIVE_REJECTED, { station_slug: slug, submission_id: row.id, creative_id: rejectTarget.creativeId, reason_codes: rejectCodes });
      setRejectTarget(null);
      setRejectReason('');
      setRejectCodes([]);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reject failed');
    } finally { setActionBusy(false); }
  };

  const productName = row && config
    ? (config.products.find((p) => p.id === row.campaign?.selectedProduct)?.name ?? row.campaign?.selectedProduct ?? '—')
    : '—';
  const marketNames = row && config
    ? (row.campaign?.targetMarkets || [])
        .map((code) => resolveDmaName(config, code))
        .join(', ') || '—'
    : '—';

  // CTV-518 / CTV-577: publisher payout is campaign budget × (1 − rev share).
  const mediaBudgetCents = row
    ? row.budget_cents ?? row.amount_cents - row.service_fee_cents - (row.tax_cents ?? 0)
    : 0;
  const mediaBudget = (mediaBudgetCents / 100).toFixed(2);
  const revShare = row?.rev_share_percent ?? null;
  const yourShare =
    revShare != null ? (Math.round(mediaBudgetCents * (1 - revShare)) / 100).toFixed(2) : null;
  const installmentMonths = row?.installments?.length ?? 0;
  const yourShareSpan = installmentMonths > 1 ? ` over ${installmentMonths} months` : '';

  // ---- Floating action bar derivation (CTV-658) ----------------------------
  const hasVoided = reviewableCreatives.some((c) => c.status === 'voided');
  // First review is all-or-nothing: once every creative is decided and at
  // least one was rejected, the only move is to send rejections (releasing the
  // whole payment hold). Partial approve + reject is not a first-review path.
  const isFinalizeReady = !!isFirstReview && hasVoided && pendingCreatives.length === 0;
  const showActionBar = isFinalizeReady || pendingCreatives.length > 0;

  // Every pending card carries its own Approve, mirroring its own Reject —
  // deciding one commercial at a time is how a reviewer actually works, and it
  // is the only way out of a mixed verdict (one rejected, others undecided),
  // where the bulk button is disabled because a standing rejection means
  // nothing gets charged.
  //
  // The charge is still confirmed exactly once. Approving the LAST undecided
  // commercial on a first review with no rejections is what triggers capture
  // server-side, so that click routes through the same confirmation modal the
  // bulk button uses — making it equivalent to "Approve all & charge". Every
  // other per-card approval is free of payment consequences: a standing
  // rejection blocks capture (the API counts voided rows as blockers), and on
  // a running campaign there is nothing left to charge.
  const perCardApproveWouldCharge =
    !!isFirstReview && !hasVoided && pendingCreatives.length === 1;

  return (
    <div className="max-w-5xl">
      <Link to={`/admin/${slug}/submissions`} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to submissions
      </Link>

        {loading ? (
          <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : !row ? (
          <p className="text-sm text-slate-700">Submission not found.</p>
        ) : (
          <>
            {/* A submission belongs to exactly one station (CTV-531), so
                this page is station-bound and the selector offers stations
                only. Changing station lands on that station's Submissions
                list rather than a detail id that does not exist there. */}
              {/* CTV-759: no scope selector. A submission belongs to exactly
                  one station (CTV-531), so there is no other station to
                  switch to here — the control would only offer to navigate
                  away from the record you opened. */}
            <AdminPageHeader
              eyebrow={row.business_info?.businessName || 'Advertiser'}
              title={row.campaign?.campaignName || row.business_info?.businessName || '—'}
              subtitle={`Submitted ${new Date(row.created_at).toLocaleString()}`}
              scopeMode="none"
              currentSlug={slug}
              actions={
                <span className={`text-xs font-medium px-2.5 py-1 rounded ${publisherStatusBadge(row.status).className}`}>
                  {publisherStatusBadge(row.status).label}
                </span>
              }
            />

            {error && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</p>}

            {row.status === 'creative_rejected' && row.rejection_reason && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <div className="font-semibold text-red-900">Rejection reason</div>
                  {row.reviewed_at && (
                    <div className="text-xs text-red-700">
                      Rejected {new Date(row.reviewed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </div>
                  )}
                </div>
                <p className="text-red-800">{row.rejection_reason}</p>
              </div>
            )}

            {/* Details strip. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Section title="Advertiser">
                <Field icon={Building2} label="Business" value={row.business_info?.businessName || '—'} />
                {row.business_info?.businessWebsite && <Field icon={Globe} label="Website" value={row.business_info.businessWebsite} />}
              </Section>

              <Section title="Campaign">
                <Field icon={DollarSign} label="Budget" value={row.campaign?.budget ? `$${formatMoneySmart(row.campaign.budget)}` : '—'} />
                <Field icon={Tv} label="Product" value={productName} />
                <Field icon={Calendar} label="Start" value={formatDate(row.campaign?.startDate)} />
                <Field icon={Calendar} label="End" value={formatDate(row.campaign?.endDate)} />
                <Field icon={MapPin} label="Markets" value={marketNames} scrollable />
              </Section>

              <Section title="Payment">
                <Field label="Campaign budget" value={`$${mediaBudget}`} />
                {yourShare != null && <Field label="Net revenue" value={`$${yourShare}${yourShareSpan}`} />}
                {row.stripe_payment_intent_id && (
                  <div className="pt-1">
                    <a href={`https://dashboard.stripe.com/test/payments/${row.stripe_payment_intent_id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 font-mono">
                      View on Stripe <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </Section>
            </div>

            {/* Creatives — one card per reviewable creative; single and multi
                render identically (CTV-658). */}
            <CreativeList
              creatives={reviewableCreatives}
              videoUrls={videoUrls}
              canApprove={!!canApprove}
              actionBusy={actionBusy}
              onApproveOne={(creativeId) => {
                if (perCardApproveWouldCharge) setChargeConfirmOpen(true);
                else void approveOneCreative(creativeId);
              }}
              onReject={(creativeId) => {
                setRejectTarget({ kind: 'creative', creativeId });
                setRejectCodes([]);
                setRejectReason('');
              }}
            />

            {/* Reserve room for the fixed action bar so it never overlaps the
                last creative card. */}
            {showActionBar && <div aria-hidden className="h-24" />}
            {showActionBar && (
              <ActionBar
                actionBusy={actionBusy}
                isFinalizeReady={isFinalizeReady}
                isFirstReview={!!isFirstReview}
                pendingCount={pendingCreatives.length}
                hasVoided={hasVoided}
                voidedCount={reviewableCreatives.filter((c) => c.status === 'voided').length}
                onApprove={() => {
                  if (isFirstReview) setChargeConfirmOpen(true);
                  else approveAll();
                }}
                onSendRejections={() => setSendRejectionsConfirmOpen(true)}
              />
            )}

            {chargeConfirmOpen && (
              <>
                <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionBusy && setChargeConfirmOpen(false)} />
                <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 p-5">
                  <h3 className="text-base font-semibold text-slate-900 mb-1">
                    {pendingCreatives.length > 1 ? 'Approve these commercials?' : 'Approve this commercial?'}
                  </h3>
                  <p className="text-sm text-slate-600 mb-5">
                    Approving accepts {pendingCreatives.length > 1 ? 'these commercials' : 'this commercial'} for air and captures the advertiser&apos;s payment. This can&apos;t be undone.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setChargeConfirmOpen(false)}
                      disabled={actionBusy}
                      className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      Not yet
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        setChargeConfirmOpen(false);
                        await approveAll();
                      }}
                      disabled={actionBusy}
                      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {actionBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                      Yes, approve &amp; charge
                    </button>
                  </div>
                </div>
              </>
            )}

            {sendRejectionsConfirmOpen && (
              <>
                <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionBusy && setSendRejectionsConfirmOpen(false)} />
                <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 p-5">
                  <h3 className="text-base font-semibold text-slate-900 mb-1">
                    Send rejections to advertiser?
                  </h3>
                  <p className="text-sm text-slate-600 mb-5">
                    We&apos;ll email the advertiser with reviewer notes for {reviewableCreatives.filter((c) => c.status === 'voided').length} rejected creative{reviewableCreatives.filter((c) => c.status === 'voided').length === 1 ? '' : 's'}, release the authorization hold, and ask the advertiser to fix the affected commercials. The submission will move to <span className="font-medium text-slate-900">creative rejected</span>.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setSendRejectionsConfirmOpen(false)}
                      disabled={actionBusy}
                      className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        setSendRejectionsConfirmOpen(false);
                        await sendRejections();
                      }}
                      disabled={actionBusy}
                      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {actionBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                      Yes, send rejections
                    </button>
                  </div>
                </div>
              </>
            )}

            {rejectTarget && (
              <>
                <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionBusy && setRejectTarget(null)} />
                <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-lg bg-white rounded-xl shadow-2xl border border-slate-200 p-5 max-h-[85vh] overflow-y-auto">
                  <h3 className="text-base font-semibold text-slate-900 mb-1">Reject this creative</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Pick every reason that applies. The advertiser will see these on their campaigns page.
                  </p>

                  <div className="space-y-2 mb-4">
                    {REJECTION_REASONS.map((r) => {
                      const checked = rejectCodes.includes(r.code);
                      return (
                        <label
                          key={r.code}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            checked ? 'border-red-300 bg-red-50/60' : 'border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setRejectCodes((prev) =>
                                e.target.checked ? [...prev, r.code] : prev.filter((c) => c !== r.code),
                              );
                            }}
                            className="mt-0.5 w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                          />
                          <span className="text-sm text-slate-900 font-medium">{r.label}</span>
                        </label>
                      );
                    })}
                  </div>

                  <label className="block">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      {rejectHasOtherCode ? 'Note (required for Other)' : 'Optional note to advertiser'}
                    </span>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder={rejectHasOtherCode
                        ? 'Describe the issue so the advertiser can fix and resubmit.'
                        : 'Anything extra you want the advertiser to know.'}
                      rows={3}
                      className="mt-1 w-full p-3 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    />
                  </label>

                  <div className="flex justify-end gap-2 mt-4">
                    <button onClick={() => setRejectTarget(null)} disabled={actionBusy} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
                    <button onClick={submitReject} disabled={actionBusy || !rejectCanSubmit} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded-md inline-flex items-center gap-1.5">
                      {actionBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                      Reject creative
                    </button>
                  </div>
                </div>
              </>
            )}
        </>
      )}
    </div>
  );
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{title}</h3>
      <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-lg p-3">{children}</div>
    </div>
  );
}

function Field({
  icon: Icon,
  label,
  value,
  scrollable = false,
}: {
  icon?: React.FC<{ className?: string }>;
  label: string;
  value: string;
  scrollable?: boolean;
}) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="text-xs text-slate-500">{label}</div>
        {value && (
          <div
            className={`text-sm text-slate-900 break-words ${
              scrollable ? 'max-h-48 overflow-y-auto pr-1' : ''
            }`}
          >
            {value}
          </div>
        )}
      </div>
    </div>
  );
}

// CTV-658: one card list for every case. A single creative renders exactly
// like a multi list of one, so the layout no longer forks on count.
function CreativeList({
  creatives,
  videoUrls,
  canApprove,
  actionBusy,
  onApproveOne,
  onReject,
}: {
  creatives: SelfServiceCreativeRow[];
  videoUrls: Record<string, string>;
  canApprove: boolean;
  actionBusy: boolean;
  onApproveOne: (id: string) => void;
  onReject: (id: string) => void;
}) {
  return (
    <div className="mb-6">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          {creatives.length === 1 ? 'Creative' : `Creatives (${creatives.length})`}
        </h2>
        {creatives.length > 1 && (
          <p className="text-xs text-slate-500">
            Review each commercial, then use the button at the bottom.
          </p>
        )}
      </div>
      <div className="space-y-4">
        {creatives.map((c) => (
          <CreativeReviewCard
            key={c.id}
            creative={c}
            videoUrl={videoUrls[c.id] ?? null}
            canApprove={canApprove}
            actionBusy={actionBusy}
            onApprove={() => onApproveOne(c.id)}
            onReject={() => onReject(c.id)}
          />
        ))}
      </div>
    </div>
  );
}

function CreativeReviewCard({
  creative,
  videoUrl,
  canApprove,
  actionBusy,
  onApprove,
  onReject,
}: {
  creative: SelfServiceCreativeRow;
  videoUrl: string | null;
  canApprove: boolean;
  actionBusy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  // CTV-732: content review only. The `<video controls>` player stays (that IS
  // the review); the Download button, the "Saves as" hint and the
  // "I uploaded this to Publica" checkbox are gone, along with the
  // library-miss banner — a failed push is ops work now, not the publisher's.
  const isPending = creative.status === 'pending_review';

  const statusChip = (() => {
    if (creative.status === 'active') {
      return <span className="text-xs font-medium px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Approved</span>;
    }
    if (creative.status === 'voided') {
      return <span className="text-xs font-medium px-2 py-0.5 rounded bg-red-100 text-red-800">Rejected</span>;
    }
    if (creative.status === 'retired') {
      return <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">Retired</span>;
    }
    return <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">Pending review</span>;
  })();

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-slate-900 truncate">{creative.filename}</span>
          {statusChip}
        </div>
        <div className="text-xs text-slate-500 whitespace-nowrap">
          Weight {(creative.segment_shares ?? []).map((s) => `${s.share}%`).join(' · ') || '—'} · {formatDate(creative.creative_start_date)} – {formatDate(creative.creative_end_date)}
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          {videoUrl ? (
            <video src={videoUrl} controls className="w-full rounded-lg bg-black aspect-video" />
          ) : creative.storage_path ? (
            <div className="bg-slate-100 rounded-lg p-6 text-center text-sm text-slate-500 aspect-video flex items-center justify-center">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading video…
            </div>
          ) : (
            <div className="bg-slate-100 rounded-lg p-6 text-center text-sm text-slate-500 aspect-video flex items-center justify-center">No video uploaded</div>
          )}
          {creative.size_bytes && (
            <div className="mt-1 text-xs text-slate-500">{(creative.size_bytes / (1024 * 1024)).toFixed(1)} MB</div>
          )}
          {creative.click_through_url && (
            <div className="mt-2">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Click-through</div>
              <a href={ensureAbsoluteUrl(creative.click_through_url)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 break-all">
                {creative.click_through_url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            </div>
          )}
        </div>
        <div>
          {creative.status === 'voided' && (
            <div className="mb-3 text-sm bg-red-50 border border-red-200 rounded p-3">
              <div className="font-semibold text-red-900 mb-1">You rejected this creative</div>
              {creative.rejection_reason_codes && creative.rejection_reason_codes.length > 0 && (
                <ul className="list-disc list-inside text-red-800 mb-1">
                  {creative.rejection_reason_codes.map((code) => (
                    <li key={code}>{REJECTION_REASONS_BY_CODE[code]?.label ?? code}</li>
                  ))}
                </ul>
              )}
              {creative.rejection_reason && (
                <p className="text-red-800 whitespace-pre-wrap">{creative.rejection_reason}</p>
              )}
            </div>
          )}
          {isPending && canApprove && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onApprove}
                disabled={actionBusy}
                aria-label={`Approve ${creative.filename || 'this commercial'}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-white border border-emerald-300 hover:bg-emerald-50 rounded-md disabled:opacity-60"
              >
                <Check className="w-4 h-4" />
                Approve
              </button>
              <button
                type="button"
                onClick={onReject}
                disabled={actionBusy}
                aria-label={`Reject ${creative.filename || 'this commercial'}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 hover:bg-red-50 rounded-md disabled:opacity-60"
              >
                <X className="w-4 h-4" />
                Reject
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionBar({
  actionBusy,
  isFinalizeReady,
  isFirstReview,
  pendingCount,
  hasVoided,
  voidedCount,
  onApprove,
  onSendRejections,
}: {
  actionBusy: boolean;
  isFinalizeReady: boolean;
  isFirstReview: boolean;
  pendingCount: number;
  hasVoided: boolean;
  voidedCount: number;
  onApprove: () => void;
  onSendRejections: () => void;
}) {
  // CTV-732: the Retry-push branch is gone, and nothing gates on upload
  // confirmations any more — the only remaining blocker is the all-or-nothing
  // first-review rule (decide every creative before charging).
  const primaryBtnBase =
    'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors';
  const primaryBtnEnabled = 'text-white bg-emerald-600 hover:bg-emerald-700';
  const primaryBtnDisabled = 'text-slate-400 bg-slate-200 cursor-not-allowed';

  let label: string;
  let onClick: () => void;
  let disabled: boolean;
  let hint: string;

  if (isFinalizeReady) {
    label = 'Send rejections to advertiser';
    onClick = onSendRejections;
    disabled = actionBusy;
    hint = `Ready — send rejections for ${voidedCount} creative${voidedCount === 1 ? '' : 's'} to release the authorization hold.`;
  } else if (isFirstReview) {
    // All-or-nothing first review: a rejection with pending remaining blocks
    // approve until every creative is decided.
    label = pendingCount > 1 ? 'Approve all & charge' : 'Approve & charge';
    onClick = onApprove;
    disabled = actionBusy || hasVoided;
    hint = hasVoided
      // The per-card Approve / Reject buttons are the way out of this state:
      // nothing is charged while a rejection stands, so deciding the rest just
      // unlocks "Send rejections to advertiser".
      ? 'Approve or reject each remaining commercial above — nothing is charged while a rejection stands.'
      : 'Ready — approve to capture payment.';
  } else {
    // Re-review of a running campaign (fresh and/or reverted swaps). No charge.
    label = hasVoided ? 'Approve remaining' : pendingCount > 1 ? 'Approve all' : 'Approve';
    onClick = onApprove;
    disabled = actionBusy;
    hint = 'Ready — approve to update the commercials on air.';
  }

  return (
    // Fixed to the viewport bottom spanning the full main column (sidebar is
    // w-60), so the bar reaches both edges at any zoom rather than being
    // clipped to the page's max-width. A spacer in the page flow reserves room
    // so it never covers the last content.
    <div className="fixed bottom-0 left-0 md:left-60 right-0 z-40 bg-white border-t border-slate-200 px-6 md:px-10 py-4 flex items-center justify-between gap-4">
      <p className="text-sm text-slate-600">{hint}</p>
      <button
        onClick={onClick}
        disabled={disabled}
        className={`${primaryBtnBase} ${disabled ? primaryBtnDisabled : primaryBtnEnabled}`}
      >
        {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {label}
      </button>
    </div>
  );
}

export default PublisherSubmissionDetailPage;
